<template>
  <div>
    <button
      v-if="!showNewSiteUser"
      id="add-new-user-btn"
      class="btn add-btn"
      @click="openNewSiteUser"
    >
      Add Site User +
    </button>

    <div
      v-if="siteUsers.length > 0 || showUpdateSiteUser || showNewSiteUser"
      class="site-user-table"
    >
      <div class="site-user-header-row">
        <div>Username</div>
        <div>Role</div>
        <div />
        <div />
      </div>
      <div
        v-if="showNewSiteUser"
        class="new-site-user-section"
      >
        <autocomplete-input
          ref="autocomplete"
          inputId="newSiteUserUsername"
          :initialValue="newSiteUser.username"
          :suggestions="usernameSuggestions"
          @getSuggestions="getUsernameSuggestions"
          @setInput="(newUsernameValue) => newSiteUser.username = newUsernameValue"
        />
        <autocomplete-input
          inputId="newSiteUserRole"
          :initialValue="newSiteUser.role"
          :suggestions="roleSuggestions"
          @getSuggestions="getRoleSuggestions"
          @setInput="(newRoleValue) => newSiteUser.role = newRoleValue"
        />
        <button
          class="btn btn--cancel cancel-btn"
          title="Cancel"
          @click="cancelNewSiteUser"
        >
          Cancel
        </button>
        <button
          class="btn btn--primary add-btn"
          title="Add"
          @click="submitNewSiteUser"
        >
          Add
        </button>
      </div>
      <div
        id="site-user-rows"
        class="site-user-body"
      >
        <div
          v-for="(user, index) in sortSiteUsers(siteUsers)"
          :id="`site-user-row-${index}`"
          :key="index"
          class="site-user-row"
        >
          <autocomplete-input
            v-if="updateSiteUserIndex === index"
            ref="autocomplete"
            inputId="updateSiteUserUsername"
            :initialValue="updateSiteUser.username"
            :suggestions="usernameSuggestions"
            @getSuggestions="getUsernameSuggestions"
            @setInput="(newUsernameValue) => updateSiteUser.username = newUsernameValue"
          />
          <div
            v-else
            class="site-user-username"
            :title="user.username"
          >
            {{ user.username }}
          </div>
          <autocomplete-input
            v-if="updateSiteUserIndex === index"
            id="updateSiteUserRole"
            :initialValue="updateSiteUser.role"
            :suggestions="roleSuggestions"
            @getSuggestions="getRoleSuggestions"
            @setInput="(newRoleValue) => updateSiteUser.role = newRoleValue"
          />
          <div
            v-else
            class="site-user-role"
            :title="user.role"
          >
            {{ user.role }}
          </div>
          <button
            v-if="updateSiteUserIndex === index"
            class="btn btn--cancel cancel-btn"
            @click="cancelUpdateSiteUser"
          >
            Cancel
          </button>
          <button
            v-else
            title="Delete"
            class="delete-btn"
            @click="removeSiteUser(user)"
          />
          <button
            v-if="updateSiteUserIndex === index"
            class="btn btn--primary add-btn"
            @click="submitUpdateSiteUser"
          >
            Save
          </button>
          <button
            v-else
            title="Edit"
            class="edit-btn"
            @click="openUpdateSiteUser(user, index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AutocompleteInput from '../AutocompleteInput'
import {getRolePrecedence} from '../utils'
import axios from 'axios'

export default {
  name: 'SiteUserTable',
  components: { AutocompleteInput },
  props: {
    initialUsers: {
      type: Array,
      default: () => []
    },
    maxSuggestions: {
      type: Number,
      default: 10
    }
  },
  emits: ['siteUsersChange'],
  data() {
    return {
      allUserRoles: [],
      emptySiteUser: {
        'username': '',
        'role': ''
      },
      newSiteUser: {},
      roleSuggestions: [],
      showNewSiteUser: false,
      updateSiteUser: {},
      updateSiteUserIndex: -1,
      siteUsers: [],
      usernameSuggestions: [],
    }
  },
  computed: {
    showUpdateSiteUser() {
      return this.updateSiteUserIndex >= 0
    }
  },
  mounted() {
    this.initialUsers.forEach(user => this.siteUsers.push(user))
    this.newSiteUser = structuredClone(this.emptySiteUser)
    this.updateSiteUser = structuredClone(this.emptySiteUser)
    axios.get('/en/topic/site_users/roles/').then(response => {
      // the list is very small and so we get all at once and filter on the frontend
      if (response.status === 200) {
        this.allUserRoles = response.data
      }
    })
  },
  methods: {
    cancelNewSiteUser() {
      this.newSiteUser = structuredClone(this.emptySiteUser)
      this.showNewSiteUser = false
    },
    cancelUpdateSiteUser() {
      this.updateSiteUserIndex = -1
      this.updateSiteUser = structuredClone(this.emptySiteUser)
    },
    getRoleSuggestions(term) {
      if (this.showNewSiteUser) {
        this.newSiteUser.role = term
      } else if (this.updateSiteUserIndex >= 0) {
        this.updateSiteUser.role = term
      }
      if (term.length >= 1) {
        this.roleSuggestions = this.allUserRoles.filter(role => role.startsWith(term)).slice(0, this.maxSuggestions)
      } else {
        this.roleSuggestions = []
      }
    },
    getUsernameSuggestions(term) {
      if (this.showNewSiteUser) {
        this.newSiteUser.username = term
      } else if (this.updateSiteUserIndex >= 0) {
        this.updateSiteUser.username = term
      }
      axios.get(`/en/autocomplete/?field=username&term=${term}&use_keyword=true`).then(response => {
        this.usernameSuggestions = response.data.splice(0, this.maxSuggestions)
      })
    },
    openNewSiteUser() {
      this.showNewSiteUser = true
      this.cancelUpdateSiteUser()
      this.$nextTick(() => {
        // need to get input element each time because it de-instantiates
        this.$refs.autocomplete.$refs.inputValue.focus()
      })
    },
    openUpdateSiteUser(siteUser, index) {
      this.updateSiteUser = structuredClone(siteUser)
      this.updateSiteUserIndex = index
      this.cancelNewSiteUser()
      this.$nextTick(() => {
        // need to get input element each time because it de-instantiates
        this.$refs.autocomplete[0].$refs.inputValue.focus()
      })
    },
    removeSiteUser(siteUser) {
      this.siteUsers = this.siteUsers.filter(user => user.username !== siteUser.username)
      this.$emit('siteUsersChange', this.siteUsers)
    },
    sortSiteUsers(siteUsers) {
      return siteUsers.toSorted((a, b) => {
        return getRolePrecedence(b.role) - getRolePrecedence(a.role)
      })
    },
    submitNewSiteUser() {
      let existingSiteUser = this.siteUsers.find(su => su.username === this.newSiteUser.username)
      if (existingSiteUser == null) {
        this.siteUsers.push(structuredClone(this.newSiteUser))
        this.$emit('siteUsersChange', this.siteUsers)
      }
      this.newSiteUser = structuredClone(this.emptySiteUser)
      this.showNewSiteUser = false
    },
    submitUpdateSiteUser() {
      this.siteUsers[this.updateSiteUserIndex] = structuredClone(this.updateSiteUser)
      this.$emit('siteUsersChange', this.siteUsers)
      this.updateSiteUser = structuredClone(this.emptySiteUser)
      this.updateSiteUserIndex = -1
    },
  }
}
</script>

<style scoped>
button#add-new-user-btn {
  background-color: #999;
  padding: 5px;
}
button.add-btn, button.cancel-btn {
  font-size: 0.85em;
}
.site-user-table {
  padding: 0 0.6rem;
  .site-user-header-row {
    font-weight: bold;
    margin-top: 0.6rem;
    border-top: 2px solid lightgrey;
  }
  .site-user-header-row, .site-user-row, .new-site-user-section {
    display: grid;
    gap: 0.6rem;
    grid-template-columns: repeat(2, 3fr) repeat(2, 1fr);
    border-bottom: 2px solid lightgrey;
    div {
      padding: 0.25rem 0.6rem 0.25rem 0.6rem;
    }
  }
  .new-site-user-section, .site-user-row {
    button {
      margin: 0.25rem auto;
    }
    .autocomplete {
      padding: 0.25rem 0;
    }
  }
  .delete-btn, .edit-btn {
    width: 32px;
    height: 32px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
  }
  .delete-btn {
    background-image: url('/static/images/icons/delete_black_48dp.svg');
  }
  .edit-btn {
    background-image: url('/static/images/icons/edit_black_48dp.svg');
  }
}
.site-user-username, .site-user-role {
  text-overflow: ellipsis;
  overflow-x: hidden;
  text-wrap: nowrap;
}

</style>
